import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationOutlined,
} from '@ant-design/icons';
import { Input, InputRef, MenuProps, Space, Tooltip } from 'antd';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { useProcManageActions } from '@/features/proc-management';
import { StepItemResponse as StepItemType } from '@/shared/api';
import { useOnClickOutside } from '@/shared/hooks';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { DndItemType, EventTypes } from '@/shared/types';
import { ContextMenu, ContextMenuRefProps, Row } from '@/shared/ui';
import { once } from '@/shared/utils';

type DragItem = {
  index: number;
  id: string;
  type: string;
};

type Props = {
  index: number;
  step: StepItemType;
  moveStepItem: (dragIndex: number, hoverIndex: number) => void;
  onItemDrop: () => void;
};

enum MenuOptions {
  EditTitle = 'editTitle',
  DuplicateStep = 'duplicateStep',
  DeleteStep = 'deleteStep',
}

const contextMenuOptionsWhenSingleStep: MenuProps['items'] = [
  {
    label: (
      <Space>
        <EditOutlined />
        Edit title
      </Space>
    ),
    key: MenuOptions.EditTitle,
  },
  {
    label: (
      <Space>
        <CopyOutlined />
        Duplicate step
      </Space>
    ),
    key: MenuOptions.DuplicateStep,
  },
];

export const ProcStepItem: FC<Props> = ({
  step,
  index,
  moveStepItem,
  onItemDrop,
}) => {
  const {
    proc,
    selectedStep,
    setSelectedStep,
    setSelectedComponent,
    setChangesSaved,
  } = useProcContext();
  const { theme } = useThemeContext();
  const { duplicateStep, updateStep, removeStep } = useProcManageActions();

  const stepRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef(null);
  const inputRef = useRef<InputRef>(null);

  const [showInput, setShowInput] = useState(false);
  const [isHovering, setIsHovering] = useState(true);
  const [stepTitle, setStepTitle] = useState(step?.title);

  const contextMenuRef = useRef<ContextMenuRefProps>(null);

  const showContextMenu = useMemo(
    () =>
      (!showInput && isHovering && selectedStep?.id === step?.id) ||
      contextMenuRef.current?.isOpen,
    [showInput, isHovering, selectedStep],
  );

  const contextMenuOptionsWhenMultipleSteps: MenuProps['items'] = useMemo(
    () => [
      {
        label: (
          <Space>
            <EditOutlined />
            Edit Title
          </Space>
        ),
        key: MenuOptions.EditTitle,
      },
      {
        label: (
          <Space>
            <CopyOutlined />
            Duplicate step
          </Space>
        ),
        key: MenuOptions.DuplicateStep,
      },
      { type: 'divider' },
      {
        label: (
          <Space>
            <DeleteOutlined />
            Delete
          </Space>
        ),
        key: MenuOptions.DeleteStep,
        style: {
          color: themeConfig[theme].colors.error,
        },
      },
    ],
    [theme],
  );

  const contextMenuOptions = useMemo(
    () =>
      proc?.stepData?.steps?.length === 1
        ? contextMenuOptionsWhenSingleStep
        : contextMenuOptionsWhenMultipleSteps,
    [proc],
  );

  // react-dnd
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: DndItemType.StepItem,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!stepRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = stepRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveStepItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    drop() {
      onItemDrop();
    },
  });

  const [{ isDragging }, drag] = useDrag(
    {
      type: 'stepItem',
      item: () => {
        return { id: step.id, index };
      },
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [step?.id, index],
  );

  const opacity = isDragging ? 0 : 1;
  drag(drop(stepRef));

  const resetInput = () => {
    setShowInput(false);
    setStepTitle(step.title);
  };

  const updateStepTitle = async () => {
    if (!stepTitle || stepTitle === step?.title) {
      resetInput();
      return;
    }
    if (selectedStep?.id) {
      updateStep({
        stepId: selectedStep.id,
        data: { title: stepTitle },
        onSuccess: () => resetInput(),
      });
    }
  };

  const handleStepRemove = async () => {
    if (selectedStep?.id) {
      const stepIndex = proc?.stepData?.steps?.findIndex(
        (step) => step.id === selectedStep?.id,
      );
      removeStep({
        stepId: selectedStep.id,
        onSuccess: () => {
          if (stepIndex! > 0) {
            const steps = proc?.stepData?.steps;
            setSelectedStep(steps![stepIndex! - 1]);
          } else {
            setSelectedStep(undefined);
          }
        },
      });
    }
  };

  const handleStepDuplicate = async () => {
    if (selectedStep?.id) {
      duplicateStep({ stepId: selectedStep.id });
    }
  };

  const toggleComments = () => {
    if (selectedStep?.id) {
      updateStep({
        stepId: selectedStep.id,
        data: { commentsEnabled: !selectedStep?.commentsEnabled },
        onSuccess: () => resetInput(),
      });
    }
  };

  const handeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      updateStepTitle();
    }
  };

  const handleStepSelect = () => {
    setChangesSaved(true);
    setSelectedComponent(undefined);
    setTimeout(() => {
      setSelectedStep(step);
    }, 20);
  };

  useOnClickOutside(inputWrapperRef, () => {
    updateStepTitle();
  });

  const onContextItemClick = useCallback(
    ({ key, domEvent }: any) => {
      domEvent.stopPropagation();
      switch (key) {
        case MenuOptions.EditTitle:
          setShowInput(true);
          break;
        case MenuOptions.DuplicateStep:
          return handleStepDuplicate();
        case MenuOptions.DeleteStep:
          return handleStepRemove();
        case 'comments':
          return toggleComments();
        default:
          return null;
      }
    },
    [selectedStep],
  );

  useEffect(() => {
    once(EventTypes.STEP_ADDED, (eventData) => {
      if (eventData.detail.step.id === selectedStep?.id) {
        setShowInput(true);
      }
    });
  }, []);

  useEffect(() => {
    setShowInput(false);
  }, [selectedStep]);

  useEffect(() => {
    setStepTitle(step?.title);
  }, [step]);

  useEffect(() => {
    if (showInput) {
      inputRef.current?.focus({
        cursor: 'all',
      });
    }
  }, [showInput]);

  return (
    <Step
      data-testid={`step-item-${index}`}
      ref={stepRef}
      data-handler-id={handlerId}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onDoubleClick={() => setShowInput(true)}
      onClick={handleStepSelect}
      style={{
        backgroundColor:
          selectedStep?.id === step?.id
            ? themeConfig[theme].colors.contentBgSecondary
            : 'transparent',
        borderBottomColor: themeConfig[theme].colors.border,
        color: themeConfig[theme].colors.text,
        opacity,
      }}
    >
      <Row justifyContent="space-between">
        <Row>
          {showInput ? (
            <div ref={inputWrapperRef}>
              <Input
                ref={inputRef}
                onKeyDown={handeKeyDown}
                autoFocus
                size="small"
                value={stepTitle}
                onChange={(e) => setStepTitle(e.target.value)}
              />
            </div>
          ) : (
            <>
              {step.components.length === 0 && (
                <Tooltip title="Step is empty" placement="right">
                  <ExclamationOutlined
                    style={{
                      position: 'absolute',
                      left: 4,
                      color: themeConfig[theme].colors.error,
                    }}
                  />
                </Tooltip>
              )}
              <StepNumber
                style={{
                  color:
                    theme === 'light'
                      ? themeConfig[theme].colors.text
                      : themeConfig[theme].colors.bgBase,
                }}
              >
                #{index + 1}
              </StepNumber>
              <span data-testid="step-title">{step?.title}</span>
            </>
          )}
        </Row>
        {showContextMenu ? (
          <ContextMenu
            data-testid="step-options-menu"
            ref={contextMenuRef}
            options={contextMenuOptions}
            onClick={(info) => onContextItemClick(info)}
          />
        ) : (
          <span style={{ width: 32 }}></span>
        )}
      </Row>
    </Step>
  );
};

const Step = styled.div`
  position: relative;
  padding: 15px 5px 15px 15px;
  width: 100%;
  display: flex;
  gap: 10px;
  font-size: 12px;
  align-items: center;
  height: 50px;
  border-bottom: 1px solid;
  cursor: pointer;
  &:hover {
    background-color: #eee;
  }
`;

const StepNumber = styled.div`
  font-family: 'Inter', sans-serif !important;
  background: #ffed66;
  padding: 5px;
  border-radius: 5px;
  font-weight: 500;
  font-size: 9px;
`;
